import { Link } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { PLAN_PILL, PLAN_TONE, planStatus } from './myTasksModel';
import { clockTime, windowLabel } from './taskLabels';

/**
 * Eingeklappter Abschnitt unter der Zeitleiste („Erledigt“, „Entfällt“, „Bei anderen“): nichts geht verloren,
 * der Plan bleibt trotzdem kurz.
 */
export function FoldSection({
  title,
  tasks,
  userId,
  timeZone,
}: {
  title: string;
  tasks: CleaningTaskQueueResponse[];
  userId: string | undefined;
  timeZone?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  if (tasks.length === 0) return null;
  return (
    <section className="border-hairline rounded-tile border bg-white">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((value) => !value)}
        className="text-ink-soft flex min-h-12 w-full items-center gap-2 px-4 text-left text-[13px] font-bold"
      >
        <span className="flex-1">
          {title} · {tasks.length}
        </span>
        <ChevronDown
          size={18}
          aria-hidden
          className={cn('transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <ul id={id} className="border-hairline border-t">
          {tasks.map((task) => {
            const status = planStatus(task, userId);
            const finished = task.status === 'Completed' || task.status === 'Issue';
            return (
              <li key={task.id} className="border-hairline border-b last:border-b-0">
                <Link
                  to="/cleaning/$id"
                  params={{ id: task.id }}
                  className="hover:bg-card-alt/50 flex min-h-12 items-center gap-2 px-4 py-2 text-sm"
                >
                  <b className="min-w-0 flex-1 truncate font-serif text-base">
                    {task.propertyName}
                  </b>
                  <span className="text-ink-soft text-xs">
                    {finished && task.completedAt
                      ? `um ${clockTime(task.completedAt, timeZone)}`
                      : windowLabel(task)}
                  </span>
                  <span className={cn(PLAN_PILL, PLAN_TONE[status.tone])}>{status.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
